import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { WorkshopContext } from "../../contexts/workshop.context";

import Popup from "./popup";
import Button from "./button";

import { AddIconWhite, HeadIcon, MagnifierIcon, PersonIcon, RadarIcon } from "../../assets/icons";
import { defaultPlatforms, iconsMappingList } from "../../util/workshop.util";
import { getInitials } from "../../util/general.util";

const iconsMap = {
  magnifier: MagnifierIcon,
  head: HeadIcon,
  person: PersonIcon,
  radar: RadarIcon
};

const MapRoundedByIndex = {
  0: "rounded-tl-2xl border-t-2 border-l-2",
  1: "rounded-tr-2xl border-t-2 border-r-2",
  2: "rounded-bl-2xl border-l-2 border-b-2",
  3: "rounded-br-2xl  border-b-2 border-r-2"
};

const mapImages = {
  mtOnline: {
    0: require(`../../assets/images/maps/online-0.png`),
    1: require(`../../assets/images/maps/online-1.png`),
    2: require(`../../assets/images/maps/online-2.png`),
    3: require(`../../assets/images/maps/online-3.png`)
  },
  allOther: {
    0: require(`../../assets/images/maps/0.png`),
    1: require(`../../assets/images/maps/1.png`),
    2: require(`../../assets/images/maps/2.png`),
    3: require(`../../assets/images/maps/3.png`)
  }
};

const MapPicker = ({
  socket,
  id,
  updateId,
  name,
  userId,
  moderator,
  summary = false,
  pdf = false,
  section
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [mapNames, setMapNames] = useState({});
  const [pendingSelection, setPendingSelection] = useState(null);
  const { workshopDetails: { platform } = {} } = useContext(WorkshopContext);

  const isOnline = platform === defaultPlatforms.mtOnline;

  useEffect(() => {
    socket.on(id, (data) => {
      try {
        if (data) {
          setMapNames(data);
        }
      } catch (error) {
        console.log(error);
      }
    });

    socket.emit(id);
  }, [id]);

  return (
    <>
      <div
        className={`grid ${section === undefined ? "grid-cols-2" : "grid-cols-1"} gap-0 ${pdf ? "w-full h-full mx-auto grid-rows-2" : ""} ${isOnline && "!grid-cols-1 md:!grid-cols-2"}`}>
        {[0, 1, 2, 3].map(({}, index) => {
          if (section !== undefined && section !== index) return null;
          return (
            <div
              key={index}
              className={`relative cursor-pointer  ${isOnline ? "m-1" : ""}`}
              onClick={() => {
                if (moderator || summary) return;
                setOpen(index);
              }}>
              <img
                src={mapImages[platform] ? mapImages[platform][index] : mapImages.allOther[index]}
                className={`block w-auto h-auto ${!summary && !isOnline && MapRoundedByIndex[index]} ${isOnline ? "rounded-2xl" : ""} border-gray-250 ${pdf ? "w-full h-full object-contain" : ""}`}
              />
              <div className="absolute top-0 left-0 w-full h-full flex flex-wrap gap-1 items-center justify-center content-center p-4">
                {mapNames[index] !== undefined &&
                  mapNames[index].map(({ id, name, symbolIndex }, symIndex) => {
                    return (
                      <Symbol
                        key={symbolIndex + "-" + symIndex}
                        name={name}
                        symbolIndex={symbolIndex}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (summary) return;

                          if (id !== userId) return;

                          socket.emit(updateId, {
                            userId,
                            name,
                            mapIndex: index,
                            symbolIndex: symbolIndex
                          });
                        }}
                      />
                    );
                  })}
                {!summary && (
                  <span
                    className="w-12 h-12 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none bg-white rounded-full bg-orange-100"
                    style={{ opacity: mapNames?.[index]?.length > 0 ? 0 : 1 }}>
                    <AddIconWhite />
                  </span>
                )}
                <div className="absolute right-0 bottom-0 flex gap-2 p-2">
                  {iconsMappingList[index].map((icon, i) => {
                    const Icon = iconsMap[icon];
                    return (
                      <span key={i} className="w-12 h-12 bg-gray-250 rounded-full p-2">
                        <Icon bg={"#454444"} iconColor={"#fff"} />
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!summary && (
        <Popup
          open={open !== false}
          setOpen={(openState) => {
            setOpen(openState);
            setPendingSelection(null);
          }}
          autoHeight>
          <div className="flex flex-col gap-4">
            <p
              className="text-lg text-black-100"
              dangerouslySetInnerHTML={{ __html: t("mtActivityFourOptions.both") }}
            />
            {!pendingSelection ? (
              <>
                <div
                  className="flex flex-row gap-2 items-center justify-start cursor-pointer"
                  onClick={() => {
                    setPendingSelection({ mapIndex: open, symbolIndex: 0 });
                  }}>
                  <span className="w-12 h-12 p-2 rounded-full bg-orange-100 text-white-100 font-bold flex items-center justify-center">
                    <span className="text-base uppercase">
                      {getInitials(name) || `${name?.charAt(0)}${name?.charAt(1)}`}
                    </span>
                  </span>
                  <p
                    className="w-[calc(100%-3rem)]"
                    dangerouslySetInnerHTML={{ __html: t("mtActivityFourOptions.one") }}
                  />
                </div>
                <div
                  className="flex flex-row gap-2 items-center justify-start cursor-pointer"
                  onClick={() => {
                    setPendingSelection({ mapIndex: open, symbolIndex: 1 });
                  }}>
                  <span className="w-12 h-12 p-2 rounded-full bg-white-100 text-black-100 border-orange-100 border-2 font-bold flex items-center justify-center">
                    <span className="text-base uppercase">
                      {getInitials(name) || `${name?.charAt(0)}${name?.charAt(1)}`}
                    </span>
                  </span>
                  <p
                    className="w-[calc(100%-3rem)]"
                    dangerouslySetInnerHTML={{ __html: t("mtActivityFourOptions.two") }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-row gap-2 items-center justify-start">
                  <span
                    className={`w-12 h-12 p-2 rounded-full ${pendingSelection.symbolIndex === 0 ? "bg-orange-100 !text-white-100" : "bg-white-100 text-black-100 border-orange-100 border-2"} text-black-100 font-bold flex items-center justify-center`}>
                    <span className="text-base uppercase">
                      {getInitials(name) || `${name?.charAt(0)}${name?.charAt(1)}`}
                    </span>
                  </span>
                  <p
                    className="w-[calc(100%-3rem)]"
                    dangerouslySetInnerHTML={{
                      __html: t(
                        `mtActivityFourOptions.${pendingSelection.symbolIndex === 0 ? "one" : "two"}`
                      )
                    }}
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    type="outline"
                    text={t("mtActivityFourOptions.cancel")}
                    className="flex-1 px-6 py-2 bg-gray-100 text-black-100 rounded-lg hover:bg-gray-200 transition-colors"
                    onClick={() => {
                      setPendingSelection(null);
                    }}
                  />
                  <Button
                    text={t("mtActivityFourOptions.submit")}
                    className="flex-1 px-6 py-2 bg-orange-100 text-white-100 rounded-lg hover:bg-orange-200 transition-colors"
                    onClick={() => {
                      socket.emit(updateId, {
                        userId,
                        name,
                        mapIndex: pendingSelection.mapIndex,
                        symbolIndex: pendingSelection.symbolIndex
                      });
                      setOpen(false);
                      setPendingSelection(null);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </Popup>
      )}
    </>
  );
};

const Symbol = ({ name, symbolIndex, onClick }) => {
  return (
    <span
      className={`relative z-10 w-12 h-12 p-2 rounded-full ${symbolIndex === 0 ? "bg-orange-100 text-white-100" : "bg-white-100 text-black-100 border-orange-100 border-2"} font-bold flex items-center justify-center`}
      onClick={onClick}>
      <span className="text-sm uppercase">{getInitials(name)}</span>
    </span>
  );
};

export default MapPicker;
